import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryDropdown, type CategoryDropdownItemProps } from '../../../components/categories/CategoryDropdown';
import { onboardingApplicationQueryOptions, useSaveWorkCategoriesMutation } from '../api/onboardingApi';
import { useWorkCategories } from '../api/workCategoriesApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState } from '../state/onboardingContext';
import { getNextStep } from '../steps';
import type { WorkCategory, WorkSubCategory } from '../types';

function leafUuidsForCategory(category: WorkCategory): string[] {
    if (category.subCategories.length === 0) {
        return [category.uuid];
    }
    return category.subCategories.flatMap((sub) => sub.items.map((item) => item.uuid));
}

function leafUuidsForSub(sub: WorkSubCategory): string[] {
    return sub.items.map((item) => item.uuid);
}

function countSelected(ids: ReadonlyArray<string>, selected: ReadonlySet<string>): number {
    return ids.reduce((sum, id) => (selected.has(id) ? sum + 1 : sum), 0);
}

export function WorkCategoriesStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: categories } = useWorkCategories();
    const { workCategories: selected, setWorkCategories: setSelected } = useOnboardingState();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveWorkCategories = useSaveWorkCategoriesMutation();

    const [openCategories, setOpenCategories] = useState<Set<string>>(() => new Set());
    const [openSubCategories, setOpenSubCategories] = useState<Set<string>>(() => new Set());

    // Prefill once from the persisted onboarding, unless the user already has a selection this session.
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current) {
            return;
        }
        if (selected.size > 0 || application?.workCategories) {
            if (selected.size === 0 && application?.workCategories) {
                setSelected(new Set(application.workCategories.childCategoryUuids));
            }
            seeded.current = true;
        }
    }, [application, selected.size, setSelected]);

    const handleNext = useCallback((): void => {
        const childCategoryUuids = Array.from(selected);
        const topCategoryUuids = categories
            .filter((category) => {
                const leaves = leafUuidsForCategory(category);
                return leaves.length > 0 && leaves.every((uuid) => selected.has(uuid));
            })
            .map((category) => category.uuid);
        saveWorkCategories.mutate(
            { topCategoryUuids, childCategoryUuids },
            {
                onSuccess: () => {
                    const next = getNextStep('work-categories');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    }, [categories, navigate, saveWorkCategories, selected]);

    const toggleOpen = useCallback((setter: typeof setOpenCategories, id: string) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleManyLeaves = useCallback(
        (ids: ReadonlyArray<string>) => {
            setSelected((prev) => {
                const next = new Set(prev);
                const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
                if (allSelected) {
                    ids.forEach((id) => next.delete(id));
                } else {
                    ids.forEach((id) => next.add(id));
                }
                return next;
            });
        },
        [setSelected],
    );

    const toggleLeaf = useCallback(
        (leafId: string) => {
            setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(leafId)) {
                    next.delete(leafId);
                } else {
                    next.add(leafId);
                }
                return next;
            });
        },
        [setSelected],
    );

    return (
        <>
            <OnboardingStepContent>
                <Stack spacing={6}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.WORK_CATEGORIES.TITLE')}
                        description={t('ONBOARDING.WORK_CATEGORIES.DESCRIPTION')}
                    />

                    <OnboardingStepError error={saveWorkCategories.error} />

                    <Stack
                        spacing={0.5}
                        component="ul"
                        sx={{ listStyle: 'none', p: 0, m: 0 }}
                        aria-label={t('ONBOARDING.WORK_CATEGORIES.LIST_ARIA')}>
                        {categories.map((category) => (
                            <li key={category.uuid}>
                                <CategoryDropdownItem
                                    category={category}
                                    open={openCategories.has(category.uuid)}
                                    openSubCategories={openSubCategories}
                                    selected={selected}
                                    onToggleCategoryOpen={(id) => toggleOpen(setOpenCategories, id)}
                                    onToggleSubOpen={(id) => toggleOpen(setOpenSubCategories, id)}
                                    onToggleManyLeaves={toggleManyLeaves}
                                    onToggleLeaf={toggleLeaf}
                                />
                            </li>
                        ))}
                    </Stack>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="work-categories"
                nextDisabled={selected.size === 0 || saveWorkCategories.isPending}
                onNext={handleNext}
            />
        </>
    );
}

type CategoryDropdownItemInnerProps = {
    category: WorkCategory;
    open: boolean;
    openSubCategories: ReadonlySet<string>;
    selected: ReadonlySet<string>;
    onToggleCategoryOpen: (id: string) => void;
    onToggleSubOpen: (id: string) => void;
    onToggleManyLeaves: (ids: ReadonlyArray<string>) => void;
    onToggleLeaf: (leafId: string) => void;
};

function CategoryDropdownItem({
    category,
    open,
    openSubCategories,
    selected,
    onToggleCategoryOpen,
    onToggleSubOpen,
    onToggleManyLeaves,
    onToggleLeaf,
}: CategoryDropdownItemInnerProps): ReactElement {
    const categoryLeafUuids = leafUuidsForCategory(category);
    const selectedInCategory = countSelected(categoryLeafUuids, selected);
    const fullyChecked = selectedInCategory === categoryLeafUuids.length && categoryLeafUuids.length > 0;
    const indeterminate = selectedInCategory > 0 && !fullyChecked;

    const items: CategoryDropdownItemProps[] = category.subCategories.map((sub) => {
        const subLeafUuids = leafUuidsForSub(sub);
        const subSelected = countSelected(subLeafUuids, selected);
        const subFullyChecked = subSelected === subLeafUuids.length && subLeafUuids.length > 0;
        return {
            uuid: sub.uuid,
            label: sub.label,
            count: subSelected,
            items: sub.items,
            open: openSubCategories.has(sub.uuid),
            checked: subFullyChecked,
            indeterminate: subSelected > 0 && !subFullyChecked,
            selected: subLeafUuids.filter((uuid) => selected.has(uuid)),
            onToggleOpen: () => onToggleSubOpen(sub.uuid),
            onToggleChecked: () => onToggleManyLeaves(subLeafUuids),
            onToggleItem: onToggleLeaf,
        };
    });

    return (
        <CategoryDropdown
            label={category.label}
            count={selectedInCategory}
            items={items}
            open={open}
            checked={fullyChecked}
            indeterminate={indeterminate}
            onToggleOpen={() => onToggleCategoryOpen(category.uuid)}
            onToggleChecked={() => onToggleManyLeaves(categoryLeafUuids)}
        />
    );
}
